import React, { useState, useRef, useEffect } from 'react'
import { ChevronDown } from '../../../assets/icons'
import { InputContainer, InputWrapper, Label, StyledInput, HelperText } from '../input/styled'
import { DropdownList, DropdownOption, ErrorMessage, SelectIcon } from '../select/styled'

const Autocomplete = ({
	label,
	name,
	value,
	onChange,
	options = [],
	placeholder = '',
	error,
	disabled = false,
	required = false,
	helperText,
	assignFieldValue,
	openOnFocus = false,
	onOpenRequest,
	isLoading = false,
	showButton = true,
	width,
	...props
}) => {
	const [isOpen, setIsOpen] = useState(false)
	const [inputValue, setInputValue] = useState('')
	const [focusedIndex, setFocusedIndex] = useState(-1)
	const isWaitingForOptions = useRef(false)
	const wrapperRef = useRef(null)
	const inputRef = useRef(null)
	const isUserTyping = useRef(false)

	useEffect(() => {
		if (!isUserTyping.current) {
			const selectedOption = options.find(opt => opt.value === value)
			const newInputValue = selectedOption
				? selectedOption.label
				: typeof value === 'string' || typeof value === 'number'
					? String(value)
					: ''
			if (inputValue !== newInputValue) {
				setInputValue(newInputValue)
			}
		}
	}, [value, options])

	useEffect(() => {
		const handleClickOutside = event => {
			const wrapperElement = wrapperRef.current
			if (
				wrapperElement &&
				typeof wrapperElement.contains === 'function' &&
				!wrapperElement.contains(event.target)
			) {
				setIsOpen(false)
				setFocusedIndex(-1)
			}
		}
		if (isOpen) document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [isOpen])

	const filteredOptions = options.filter(option => {
		const label = option && option.label ? option.label.toString() : ''
		const search = inputValue ? inputValue.toString() : ''
		return label.toLowerCase().includes(search.toLowerCase())
	})

	useEffect(() => {
		if (!isWaitingForOptions.current) return
		if (isLoading) return

		setIsOpen(true)
		setFocusedIndex(-1)
		isWaitingForOptions.current = false
	}, [options, isLoading])

	const requestDropdownOpen = () => {
		if (disabled || !onOpenRequest) return false

		const shouldWaitForOptions = onOpenRequest()

		if (shouldWaitForOptions) {
			isWaitingForOptions.current = true
		}

		return shouldWaitForOptions
	}

	const handleInputChange = e => {
		const val = e.target.value
		isUserTyping.current = true
		setInputValue(val)
		setIsOpen(true)
		setFocusedIndex(-1)
	}

	const handleOptionClick = option => {
		isUserTyping.current = false
		setInputValue(option.label)
		setIsOpen(false)
		setFocusedIndex(-1)

		if (assignFieldValue) {
			assignFieldValue(option.value)
		} else if (onChange) {
			onChange({ target: { name, value: option.value } })
		}
	}

	const handleClear = e => {
		e.preventDefault()
		e.stopPropagation()
		isUserTyping.current = false
		setInputValue('')
		setIsOpen(false)
		setFocusedIndex(-1)

		if (assignFieldValue) {
			assignFieldValue('')
		} else if (onChange) {
			onChange({ target: { name, value: '' } })
		}

		const activeElement = document.activeElement
		if (activeElement && typeof activeElement.blur === 'function') {
			activeElement.blur()
		}
	}

	const handleToggleDropdown = e => {
		e.preventDefault()
		e.stopPropagation()
		if (disabled) return

		if (isOpen) {
			setIsOpen(false)
			setFocusedIndex(-1)
			return
		}

		if (inputRef.current) {
			inputRef.current.focus()
		}

		if (isWaitingForOptions.current) return

		const shouldWaitForOptions = requestDropdownOpen()

		if (shouldWaitForOptions) {
			setFocusedIndex(-1)
			return
		}

		setIsOpen(true)
		setFocusedIndex(-1)
	}

	const handleFocus = () => {
		if (openOnFocus) {
			const shouldWaitForOptions = requestDropdownOpen()

			if (shouldWaitForOptions || isWaitingForOptions.current) {
				setFocusedIndex(-1)
				return
			}
		}

		if (filteredOptions.length > 0) setIsOpen(true)
	}

	const handleBlur = () => {
		isUserTyping.current = false
		setTimeout(() => setIsOpen(false), 120)
	}

	const handleKeyDown = e => {
		if (!isOpen) return
		switch (e.key) {
			case 'ArrowDown':
				e.preventDefault()
				setFocusedIndex(prev => (prev < filteredOptions.length - 1 ? prev + 1 : 0))
				break
			case 'ArrowUp':
				e.preventDefault()
				setFocusedIndex(prev => (prev > 0 ? prev - 1 : filteredOptions.length - 1))
				break
			case 'Enter':
				if (focusedIndex >= 0) {
					e.preventDefault()
					handleOptionClick(filteredOptions[focusedIndex])
				}
				break
			case 'Escape':
				setIsOpen(false)
				setFocusedIndex(-1)
				break
			default:
				break
		}
	}

	return (
		<div ref={wrapperRef} style={{ width: width || '100%' }}>
			<InputWrapper style={{ width: '100%' }}>
			{label && (
				<Label htmlFor={name}>
					{label}
					{required && <span className="req">*</span>}
				</Label>
			)}
			<InputContainer style={{ position: 'relative' }} width="100%">
				<StyledInput
					width="100%"
					innerRef={inputRef}
					id={name}
					name={name}
					value={inputValue}
					placeholder={placeholder}
					onChange={handleInputChange}
					onFocus={handleFocus}
					onBlur={handleBlur}
					onKeyDown={handleKeyDown}
					disabled={disabled}
					autoComplete="off"
					$hasRight
					$error={!!error}
					style={{ paddingRight: showButton ? 72 : undefined }}
					{...props}
				/>
				<div
					style={{
						position: 'absolute',
						right: 12,
						top: '50%',
						transform: 'translateY(-50%)',
						display: 'flex',
						alignItems: 'center',
						gap: '8px',
						zIndex: 2
					}}
				>
					{showButton && inputValue && !disabled && (
						<button
							type="button"
							onMouseDown={handleClear}
							aria-label="Limpiar selección"
							style={{
								background: 'none',
								border: 'none',
								padding: '2px',
								cursor: 'pointer',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								opacity: 0.6,
								transition: 'opacity 0.2s',
								pointerEvents: 'auto'
							}}
							onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
							onMouseLeave={e => (e.currentTarget.style.opacity = '0.6')}
						>
							✕
						</button>
					)}
					<SelectIcon
						$isOpen={isOpen}
						onMouseDown={handleToggleDropdown}
						style={{
							position: 'static',
							transform: isOpen ? 'rotate(180deg)' : 'none',
							cursor: disabled ? 'default' : 'pointer',
							pointerEvents: 'auto'
						}}
					>
						<ChevronDown />
					</SelectIcon>
				</div>
				{isOpen && (
					<DropdownList style={{ width: '100%' }}>
						{filteredOptions.length === 0 ? (
							<DropdownOption disabled>Sin resultados</DropdownOption>
						) : (
							filteredOptions.map((option, idx) => (
								<DropdownOption
									key={option.value}
									onMouseDown={e => {
										e.preventDefault()
										handleOptionClick(option)
									}}
									$isSelected={idx === focusedIndex}
									style={idx === focusedIndex ? { background: '#e0e0e0', fontWeight: 600 } : {}}
									tabIndex={-1}
								>
									{option.label}
								</DropdownOption>
							))
						)}
					</DropdownList>
				)}
			</InputContainer>
			{error && <ErrorMessage>{error}</ErrorMessage>}

			{helperText && <HelperText>{helperText}</HelperText>}
			</InputWrapper>
		</div>
	)
}

export default Autocomplete
